import type { Meta, StoryObj } from '@storybook/angular';
import { signal, computed } from '@angular/core';
import { expect, within } from 'storybook/test';
import { PriceTableComponent, PriceLine } from '../../../app/content/layout/components/product/price-table/price-table.component';
import { ProductService } from '../../../app/core/services/product.service';
import { ProductPriceService } from '../../../app/core/services/product-price.service';
import { GroupVoucherService } from '../../../app/core/services/groupVoucher.service';
import { withCommonDecorators } from '../../_utils/story-helpers';
import {
  createDamilanoBarberaProduct,
  createDiscountedProduct,
  createQuantityPricedProduct,
  createGroupVoucherProduct,
} from '../../_mocks';

/**
 * Helper: builds a MockProductPriceService with real-looking price lines.
 */
function createPriceLineMocks(lines: PriceLine[]) {
  return {
    provide: ProductPriceService,
    useValue: {
      currencyCode: signal('DKK'),
      getUnitName: () => 'fl.',
      calculateDiscountPercentage: () => 0,
      formatPriceLines: () => lines,
    },
  };
}

function createProductServiceMock(lines: PriceLine[]) {
  return {
    provide: ProductService,
    useValue: {
      getFormattedProductPricesSignal: () => signal(lines),
      getDefaultPriceSignal: () => computed(() => lines[0]?.price),
    },
  };
}

const singlePriceLines: PriceLine[] = [
  { isDiscounted: false, label: '1 fl.', price: 8900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
];

const volumePriceLines: PriceLine[] = [
  { isDiscounted: false, label: '1-5 fl.', price: 8900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
  { isDiscounted: true, label: '6-11 fl.', price: 7900, currencyCode: 'DKK', qty: 6, unitName: 'fl.' },
  { isDiscounted: true, label: '12+ fl.', price: 6900, currencyCode: 'DKK', qty: 12, unitName: 'fl.' },
];

const discountPriceLines: PriceLine[] = [
  { isDiscounted: true, isStriked: true, label: '1 fl.', price: 8900, currencyCode: 'DKK', qty: 1, unitName: 'fl.' },
  { isDiscounted: true, prefix: ['Tilbudspris'], label: '1 fl.', price: 6900, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
];

const groupVoucherPriceLines: PriceLine[] = [
  { isDiscounted: true, isStriked: true, label: '1 fl.', price: 8900, currencyCode: 'DKK', qty: 1, unitName: 'fl.' },
  { isDiscounted: true, prefix: ['Storkøbspris'], label: '1 fl.', price: 6500, currencyCode: 'DKK', showVatText: true, qty: 1, unitName: 'fl.' },
];

const sekPriceLines: PriceLine[] = [
  { isDiscounted: false, label: '1 fl.', price: 9900, currencyCode: 'SEK', showVatText: true, qty: 1, unitName: 'fl.' },
];

const meta: Meta<PriceTableComponent> = {
  title: 'Molecules/Product/Price Table',
  component: PriceTableComponent,
  parameters: {
    docs: {
      description: {
        component: 'Displays product pricing with support for volume discounts, campaign discounts, group voucher prices, and add-to-cart functionality.',
      },
    },
    layout: 'padded',
  },
};

export default meta;
type Story = StoryObj<PriceTableComponent>;

export const SinglePrice: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(singlePriceLines),
      createProductServiceMock(singlePriceLines),
    ]),
  ],
  args: {
    product: createDamilanoBarberaProduct() as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/inkl\. moms/i)).toBeInTheDocument();
  },
};

export const VolumePricing: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(volumePriceLines),
      createProductServiceMock(volumePriceLines),
    ]),
  ],
  args: {
    product: createQuantityPricedProduct() as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    const canvas = within(canvasElement);
    await expect(canvas.getByText(/6-11 fl\./)).toBeInTheDocument();
    await expect(canvas.getByText(/12\+ fl\./)).toBeInTheDocument();
  },
};

export const DiscountPrice: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(discountPriceLines),
      createProductServiceMock(discountPriceLines),
    ]),
  ],
  args: {
    product: createDiscountedProduct() as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.is-discounted')).toBeTruthy();
  },
};

export const GroupVoucherPrice: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(groupVoucherPriceLines),
      createProductServiceMock(groupVoucherPriceLines),
      {
        provide: GroupVoucherService,
        useValue: {
          groupVoucherSignal: signal({
            groupVoucher: {
              code: 'FIRMAVIN2026',
              prices: [{ vismaId: 'VP-GV-100', price: '6500' }],
            },
            loading: false,
          }).asReadonly(),
          currentGroupVoucher: () => ({
            code: 'FIRMAVIN2026',
            prices: [{ vismaId: 'VP-GV-100', price: '6500' }],
          }),
        },
      },
    ]),
  ],
  args: {
    product: createGroupVoucherProduct() as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.is-discounted')).toBeTruthy();
  },
};

export const SEKCurrency: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(sekPriceLines),
      createProductServiceMock(sekPriceLines),
    ]),
  ],
  args: {
    product: createDamilanoBarberaProduct() as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-price-table')).toBeTruthy();
  },
};

export const OutOfStock: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(singlePriceLines),
      createProductServiceMock(singlePriceLines),
    ]),
  ],
  args: {
    product: createDamilanoBarberaProduct({ inStock: false, stock: 0, canBeAddedToCart: false }) as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-price-table')).toBeTruthy();
  },
};

export const HiddenPriceTable: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks([]),
      createProductServiceMock([]),
    ]),
  ],
  args: {
    product: createDamilanoBarberaProduct({ hidePriceTable: true }) as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('.price-line')).toBeFalsy();
  },
};

export const WithCtaLink: Story = {
  decorators: [
    ...withCommonDecorators([
      createPriceLineMocks(singlePriceLines),
      createProductServiceMock(singlePriceLines),
    ]),
  ],
  args: {
    product: createDamilanoBarberaProduct({ ctaLink: 'https://supervin.dk', ctaTitle: 'Køb direkte' }) as any,
    eventSourceName: 'storybook',
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-price-table')).toBeTruthy();
  },
};
